/**
 * VMActionButtons — Start/Stop/Destroy/Snapshot with confirmation dialogs
 */

import React, { useState } from 'react';
import { useInfraStore } from '@/stores/infrastructure/infra-store';
import type { VMInfo } from '@/services/api/infrastructureApi';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Play, Square, Trash2, Camera } from 'lucide-react';

interface Props {
  hostId: string;
  vm: VMInfo;
}

export const VMActionButtons: React.FC<Props> = ({ hostId, vm }) => {
  const { startVM, stopVM, destroyVM, snapshotVM } = useInfraStore();
  const [confirmAction, setConfirmAction] = useState<'stop' | 'destroy' | null>(null);
  const [showSnapshot, setShowSnapshot] = useState(false);
  const [snapshotName, setSnapshotName] = useState('');
  const [acting, setActing] = useState(false);

  const handleAction = async (action: () => Promise<boolean>) => {
    setActing(true);
    await action();
    setActing(false);
    setConfirmAction(null);
    setShowSnapshot(false);
    setSnapshotName('');
  };

  return (
    <>
      <div className="flex items-center gap-1">
        {vm.state === 'stopped' && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleAction(() => startVM(hostId, vm.id))}
            disabled={acting}
            title="Start VM"
          >
            <Play className="h-4 w-4 text-green-600" />
          </Button>
        )}
        {vm.state === 'running' && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmAction('stop')}
            disabled={acting}
            title="Stop VM"
          >
            <Square className="h-4 w-4 text-yellow-600" />
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowSnapshot(true)}
          disabled={acting}
          title="Snapshot VM"
        >
          <Camera className="h-4 w-4 text-blue-600" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setConfirmAction('destroy')}
          disabled={acting}
          title="Destroy VM"
        >
          <Trash2 className="h-4 w-4 text-red-600" />
        </Button>
      </div>

      {/* Stop Confirmation */}
      <AlertDialog open={confirmAction === 'stop'} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop VM "{vm.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will gracefully shut down the virtual machine. Use force stop if the VM is unresponsive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => handleAction(() => stopVM(hostId, vm.id, true))}
              disabled={acting}
            >
              Force Stop
            </Button>
            <AlertDialogAction
              onClick={() => handleAction(() => stopVM(hostId, vm.id))}
              disabled={acting}
            >
              Graceful Stop
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Destroy Confirmation */}
      <AlertDialog open={confirmAction === 'destroy'} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Destroy VM "{vm.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the virtual machine and all its disks. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleAction(() => destroyVM(hostId, vm.id))}
              disabled={acting}
            >
              Destroy
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Snapshot Dialog */}
      <AlertDialog open={showSnapshot} onOpenChange={() => setShowSnapshot(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Snapshot VM "{vm.name}"</AlertDialogTitle>
            <AlertDialogDescription>
              Create a point-in-time snapshot of this virtual machine.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Input
              placeholder="Snapshot name"
              value={snapshotName}
              onChange={(e) => setSnapshotName(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleAction(() => snapshotVM(hostId, vm.id, snapshotName || `snapshot-${Date.now()}`))}
              disabled={acting || !snapshotName.trim()}
            >
              Create Snapshot
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
